import { Container, Graphics, type Sprite } from 'pixi.js';
import type { EffectClip } from './effectsArt';
import { UoHueSprite } from './uoHueBatch';

/**
 * The renderer-side sprite for a **moongate** entity (The Wandering Moongate,
 * PRD #362 Slice A) - the moongate analogue of {@link DiceSprite}: a
 * double-clickable billboard pinned to its tile, but *animated*: the gate loops
 * the baked `moongate` effect strip (the blue moongate's AnimData chain), and a
 * freshly-spawned gate first plays the one-shot `moongate-flash` summon strip
 * before settling into the loop.
 *
 * Like the dice cup it needs exactly two input affordances - double-click
 * detection (enter the gate) and the long-press suppression latch (so a hold that
 * opened the context menu never *also* enters) - and none of the walk-cycle or
 * overhead-label machinery a character carries. When the effect strips are
 * unbaked (a clientless host whose stubs pre-date the effect, or a warn-skipped
 * bake) the sprite degrades to a Graphics-primitive blue oval, never a crash -
 * the effects-art graceful-absence discipline.
 */

/** Double-click window, matching {@link DiceSprite}'s generous UO-range pairing. */
const DOUBLE_CLICK_MS = 300;

/** Fallback oval dimensions (px) when the gate strip is unbaked. */
const FALLBACK_WIDTH = 20;
const FALLBACK_HEIGHT = 44;
/** Fallback moongate blue (the classic gate's dominant hue). */
const FALLBACK_COLOR = 0x3355ee;

export class MoongateSprite extends Container {
  private readonly gateClip?: EffectClip;
  private flashClip?: EffectClip;
  private readonly billboard?: Sprite;
  /** Elapsed ms into the current strip (flash first if present, then the loop). */
  private elapsedMs = 0;
  /** `performance.now()` of the last single tap, for the double-click detector. */
  private lastTapMs = Number.NEGATIVE_INFINITY;

  /**
   * Fired when the gate is **double-clicked** (within {@link DOUBLE_CLICK_MS}).
   * The renderer wires it to send an `EnterMoongate` up the socket; the gate
   * never teleports locally (the server is authoritative).
   */
  onDoubleClick?: () => void;

  /**
   * Whether the current tap should be **suppressed** because it was really a
   * long-press that already opened the in-world context menu (ADR-0169) - the
   * same latch the dice cup consults, so a hold over the gate opens the "Enter
   * the moongate" menu but never feeds the double-click detector.
   */
  suppressClick?: () => boolean;

  /**
   * `gateClip` is the looping strip, `flashClip` the optional one-shot summon
   * intro (played once through, then dropped). Either may be absent (unbaked
   * host): no gate strip means the Graphics fallback; no flash means the gate
   * starts straight on its loop.
   */
  constructor(gateClip?: EffectClip, flashClip?: EffectClip) {
    super();
    this.gateClip = gateClip && gateClip.frames.length > 0 ? gateClip : undefined;
    this.flashClip = flashClip && flashClip.frames.length > 0 ? flashClip : undefined;

    if (this.gateClip) {
      const first = (this.flashClip ?? this.gateClip).frames[0]!;
      const billboard = new UoHueSprite();
      billboard.texture = first.texture;
      // Bottom-centre, exactly like a static/door/dice billboard, so the gate
      // stands on its tile's near corner once the container is positioned there.
      billboard.anchor.set(0.5, 1);
      billboard.eventMode = 'static';
      billboard.cursor = 'inherit';
      this.addChild(billboard);
      this.billboard = billboard;
    } else {
      // Graceful absence: a legible blue oval standing on the tile.
      const oval = new Graphics()
        .ellipse(0, -FALLBACK_HEIGHT / 2, FALLBACK_WIDTH / 2, FALLBACK_HEIGHT / 2)
        .fill({ color: FALLBACK_COLOR, alpha: 0.85 });
      oval.eventMode = 'static';
      oval.cursor = 'inherit';
      this.addChild(oval);
    }

    // The container is the hit-test target; a `pointertap` folds into the
    // double-click detector (the DiceSprite pattern verbatim).
    this.eventMode = 'static';
    this.cursor = 'inherit';
    this.on('pointertap', () => {
      if (this.suppressClick?.()) return;
      this.registerTap();
    });
  }

  /** Fold one tap into the double-click detector (the DiceSprite pattern). */
  private registerTap(): void {
    const now = performance.now();
    if (now - this.lastTapMs <= DOUBLE_CLICK_MS) {
      this.lastTapMs = Number.NEGATIVE_INFINITY;
      this.onDoubleClick?.();
    } else {
      this.lastTapMs = now;
    }
  }

  /**
   * Advance the animation on the render clock: while the summon flash is active,
   * step through it once and drop it at its end; then loop the gate strip
   * forever. The Graphics fallback has nothing to animate.
   */
  update(dtMs: number): void {
    if (!this.billboard || !this.gateClip) return;
    this.elapsedMs += dtMs;

    if (this.flashClip) {
      const frame = Math.floor(this.elapsedMs / this.flashClip.frameIntervalMs);
      if (frame < this.flashClip.frames.length) {
        this.billboard.texture = this.flashClip.frames[frame]!.texture;
        return;
      }
      // Flash finished: hand over to the loop from its first frame.
      this.flashClip = undefined;
      this.elapsedMs = 0;
    }

    const loop = this.gateClip;
    const frame = Math.floor(this.elapsedMs / loop.frameIntervalMs) % loop.frames.length;
    this.billboard.texture = loop.frames[frame]!.texture;
  }

  /** Tear down the gate (it left the world - entered, fizzled, or despawned). */
  dispose(): void {
    this.destroy({ children: true });
  }
}
